import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { formatApiError, useAuth } from "../context/AuthContext";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1784031208107-f489c769e1f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHxfHxoaWdoJTIwZmFzaGlvbiUyMG1vZGVsJTIwZWRpdG9yaWFsJTIwc3RyZWV0d2VhciUyMGxvb2tib29rfGVufDB8fHx8MTc4OTk1MzM1NHww&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "signin") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="auth-page" className="pt-16 min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={SIDE_IMG} alt="Noir Archive editorial" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0C]" />
        <p className="absolute bottom-12 left-12 font-editorial italic text-3xl text-zinc-200 max-w-sm">
          "Your archive remembers you."
        </p>
      </div>

      <div className="flex items-center justify-center px-4 sm:px-8 py-16">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase">
            {mode === "signin" ? "Welcome Back" : "Join the Archive"}
          </p>
          <h1 className="font-display font-extrabold tracking-tighter uppercase text-3xl sm:text-4xl mt-3">
            {mode === "signin" ? "Sign In" : "Create Account"}
          </h1>

          <form onSubmit={submit} className="mt-10 flex flex-col gap-5" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2">Name</label>
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full bg-transparent border border-zinc-700 px-4 py-3.5 text-sm focus:outline-none focus:border-[#FF334B]"
                  placeholder="Your name"
                />
              </div>
            )}
            <div>
              <label className="mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-transparent border border-zinc-700 px-4 py-3.5 text-sm focus:outline-none focus:border-[#FF334B]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full bg-transparent border border-zinc-700 px-4 py-3.5 text-sm focus:outline-none focus:border-[#FF334B]"
                placeholder={mode === "register" ? "8+ characters" : "Your password"}
              />
            </div>

            {error && (
              <p data-testid="auth-error-message" className="text-[#FF334B] text-sm border border-[#FF334B]/40 bg-[#FF334B]/10 px-4 py-3">
                {error}
              </p>
            )}

            <button
              data-testid="auth-submit-button"
              disabled={loading}
              className="bg-[#FF334B] text-white py-4 mono text-xs tracking-[0.25em] uppercase hover:bg-[#d92339] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {loading ? "One moment…" : mode === "signin" ? "Sign In" : "Create Account"} <ArrowRight size={14} />
            </button>
          </form>

          <button
            data-testid="auth-toggle-mode-button"
            onClick={() => {
              setMode(mode === "signin" ? "register" : "signin");
              setError("");
            }}
            className="mt-8 mono text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#FF334B] transition-colors"
          >
            {mode === "signin" ? "No account? Create one" : "Already a member? Sign in"}
          </button>

          <p className="mt-10 text-zinc-600 text-xs">
            Signed-in shoppers get one-tap checkout and instant order history.{" "}
            <Link to="/shop" data-testid="auth-continue-guest-link" className="text-zinc-400 underline underline-offset-4 hover:text-[#FF334B]">
              Continue as guest
            </Link>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
